// Chuỗi xuất / nhập save (TECH_BASELINE §7.4):
//   HTTD1.<base64url(gzip(JSON))>.<crc32 hex của JSON>   khi có CompressionStream
//   HTTD0.<base64url(JSON)>.<crc32 hex của JSON>         khi không có
import type { SaveDoc } from './types';

export class CodecError extends Error {
  constructor(
    readonly reason: 'prefix' | 'format' | 'crc' | 'decompress' | 'json',
    message: string,
  ) {
    super(message);
    this.name = 'CodecError';
  }
}

let CRC_TABLE: Uint32Array | null = null;
function crcTable(): Uint32Array {
  if (CRC_TABLE) return CRC_TABLE;
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  CRC_TABLE = t;
  return t;
}

export function crc32(bytes: Uint8Array): number {
  const t = crcTable();
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = (t[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8)) >>> 0;
  return (c ^ 0xffffffff) >>> 0;
}

export function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(s: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]*$/.test(s)) throw new CodecError('format', 'payload is not base64url');
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function pipe(bytes: Uint8Array, stream: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const res = new Response(new Blob([bytes as Uint8Array<ArrayBuffer>]).stream().pipeThrough(stream));
  return new Uint8Array(await res.arrayBuffer());
}

export function hasCompression(): boolean {
  return typeof CompressionStream === 'function' && typeof DecompressionStream === 'function';
}

const hex8 = (n: number): string => n.toString(16).padStart(8, '0');

export async function encodeSave(doc: SaveDoc, opts: { compress?: boolean } = {}): Promise<string> {
  const json = new TextEncoder().encode(JSON.stringify(doc));
  const crc = hex8(crc32(json));
  const compress = opts.compress ?? hasCompression();
  if (compress) return `HTTD1.${toBase64Url(await pipe(json, new CompressionStream('gzip')))}.${crc}`;
  return `HTTD0.${toBase64Url(json)}.${crc}`;
}

/** Giải chuỗi thành doc thô (chưa di trú, chưa kiểm hình). */
export async function decodeSave(s: string): Promise<unknown> {
  const parts = s.trim().split('.');
  if (parts.length !== 3) throw new CodecError('format', 'expected 3 dot-separated parts');
  const [prefix, payload, crcHex] = parts as [string, string, string];
  if (prefix !== 'HTTD1' && prefix !== 'HTTD0') throw new CodecError('prefix', `unknown prefix ${prefix}`);
  if (!/^[0-9a-f]{8}$/.test(crcHex)) throw new CodecError('format', 'bad crc field');
  let json: Uint8Array;
  if (prefix === 'HTTD1') {
    if (!hasCompression()) throw new CodecError('decompress', 'DecompressionStream unavailable');
    try {
      json = await pipe(fromBase64Url(payload), new DecompressionStream('gzip'));
    } catch (e) {
      if (e instanceof CodecError) throw e;
      throw new CodecError('decompress', 'gzip payload is corrupt');
    }
  } else {
    json = fromBase64Url(payload);
  }
  if (hex8(crc32(json)) !== crcHex) throw new CodecError('crc', 'checksum mismatch');
  try {
    return JSON.parse(new TextDecoder().decode(json)) as unknown;
  } catch {
    throw new CodecError('json', 'payload is not JSON');
  }
}
